import type { TakSaEvent, TakSaResponse } from '../types/tak-api.js';

/** Normalized SA event with guaranteed lat/lon */
export interface NormalizedSaEvent {
  uid: string;
  type: string;
  callsign: string;
  lat: number;
  lon: number;
}

/**
 * Parse the TAK SA response into a normalized array.
 * TAK returns CoT SA in various structures — this handles all known formats.
 */
export function parseCotSaResponse(data: unknown): NormalizedSaEvent[] {
  const events = extractEvents(data);
  return events
    .map(normalizeEvent)
    .filter((evt): evt is NormalizedSaEvent => evt !== null);
}

/** Extract the events array from TAK's varying response shapes */
function extractEvents(data: unknown): TakSaEvent[] {
  if (Array.isArray(data)) return data as TakSaEvent[];

  if (!data || typeof data !== 'object') return [];

  const response = data as TakSaResponse;

  if (response.events) {
    return Array.isArray(response.events) ? response.events : [response.events];
  }

  if (response.event) {
    return Array.isArray(response.event) ? response.event : [response.event];
  }

  return [];
}

/** Normalize a single SA event, returning null if coordinates are missing */
function normalizeEvent(evt: TakSaEvent): NormalizedSaEvent | null {
  const lat = parseCoord(evt.lat ?? evt.point?.lat);
  const lon = parseCoord(evt.lon ?? evt.point?.lon);

  if (lat === null || lon === null) return null;

  return {
    uid: evt.uid ?? 'unknown',
    type: evt.type ?? '',
    callsign: evt.callsign ?? evt.uid ?? 'CoT',
    lat,
    lon,
  };
}

/** Parse a coordinate value, returning null if invalid */
function parseCoord(value: number | string | undefined): number | null {
  if (value === undefined || value === null) return null;
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return null;
  return num;
}
